import {
  blockLevelDb,
  TRANSCRIPT_CHUNK_MIN_BYTES,
  TRANSCRIPT_CHUNK_MIN_MS,
  TRANSCRIPT_CHUNK_MS,
  VoiceGate,
} from '@avslack/shared';

/**
 * Ennyivel kesleltetjuk a hangot a kapu mogott (mp).
 *
 * A kapu a KESLELTETES NELKULI jelet figyeli, a felvetel a kesleltetettet
 * kapja: igy mire a hang a kapuhoz er, a dontes mar megszuletett - a szo eleje
 * nem vagodik le.
 */
const GATE_DELAY_S = 0.3;
/** Ennyi beszed alatt a darab nem megy fel: nincs benne mit atirni. */
const MIN_SPEECH_MS = 700;
/**
 * MONDATONKENTI DARABOLAS: ha ennyi beszed mar van a darabban, a kovetkezo
 * szunetnel lezarjuk.
 *
 * Enelkul a darabok 25 masodpercesek voltak, es az atirat a darabokat EGESZBEN
 * rakta sorba: ket beszelo 25 masodpercen beluli oda-vissza valtasa
 * osszekeveredett (a valasz a kerdes kozepebe kerult). Mondatonkenti darabbal
 * a sorrend a valodi, es a vagas szunetbe esik, nem egy szo kozepebe.
 * Rovidebb beszed utani szunetnel nem zarunk: a nagyon rovid darab ("igen")
 * a felismeronek kevés kontextus.
 */
const UTTERANCE_MIN_SPEECH_MS = 1500;

/**
 * A sajat mikrofon felvetele, darabokban.
 *
 * **Mindenki a SAJAT hangjat veszi fel** - nem a media-szerver egyetlen,
 * osszekevert savot. Ezert tudjuk pontosan, ki beszelt: nem beszelo-
 * szetvalasztas talalgatja ki, hanem a darab gazdaja mondja meg.
 *
 * ─── Miert allunk meg es indulunk ujra minden darabnal? ──────────────────────
 *
 * A `MediaRecorder.start(idoszelet)` folyamatosan darabolna, DE csak az elso
 * darab tartalmazza a fajl fejlecet - a tobbi onmagaban nem megnyithato.
 * A felismero ezekbol semmit nem tudna kezdeni, es a hiba NEMA lenne: a
 * felvetel menne, a feltoltes sikerulne, az atirat vegen pedig nem lenne
 * szoveg. Ezert minden darab kulon felvetel: `stop()`, majd azonnal `start()`.
 * Az ara egy ezredmasodperces res a darabok kozott.
 *
 * ─── Miert csak nyitott mikrofonnal? ─────────────────────────────────────────
 *
 * Nemitva a sav csendet ad. Azt feltolteni sávszélesség- es
 * processzor-pazarlas, a felismerok pedig a csendbe hajlamosak beleképzelni
 * mondatokat. A felvetel ezert a mikrofonnal egyutt indul es all le.
 */

/**
 * Amit a bongeszo tenylegesen tud.
 *
 * A sorrend szandekos: az Opus a legjobb arany meret es minoseg kozott. Ha
 * egyik sem megy, a felvetel el sem indul - es ezt megmondjuk, nem csendben
 * hagyjuk ki.
 */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'] as const;

export function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

export interface RecordedChunk {
  blob: Blob;
  /** A HIVAS kezdetetol szamolva - ebbol lesz a kozos idovonal. */
  startMs: number;
  durationMs: number;
  /**
   * AMIVEL felvettuk.
   *
   * A felismero a fajlnev kiterjesztesebol is kovetkeztet a formatumra, ezert
   * a szervernek tudnia kell, mit kapott: egy Ogg-darabot `.webm` neven
   * bekuldve ugyanaz a "Failed to decode audio." jon vissza, mint egy serult
   * fajlnal. Bongeszonkent kulonbozik, tehat nem talalgatni kell.
   */
  mimeType: string;
}

export interface RecorderOptions {
  stream: MediaStream;
  /**
   * Hany ezredmasodperc telt el a hivas kezdete ota - MOST.
   *
   * A hivo szamolja, mert csak o tudja, mikor kezdodott a hivas. A sajat
   * orank es a szerver oraja kozotti elteres ennyivel eltolja ennek a
   * resztvevonek a szakaszait; NTP mellett ez masodperc alatti, es a
   * szakaszok sorrendjet a resztvevon belul nem bantja.
   */
  offsetMs: () => number;
  onChunk: (chunk: RecordedChunk) => void;
  /** A felvetel elakadt. A felulet ezt KIIRJA - nem csendben hagyjuk abba. */
  onError: (message: string) => void;
  /**
   * BESZEDKAPU (alapbol be): csak a mikrofon gazdajanak beszede kerul a
   * felvetelbe - a csend es a szomszed atszurodo hangja nem. Lasd voice-gate.ts.
   */
  gate?: boolean;
}

/** A kapu hangfeldolgozasa. Ha a bongeszo nem tudja felepiteni, kapu nelkul veszunk fel. */
interface GatePipeline {
  context: AudioContext;
  stream: MediaStream;
  gain: GainNode;
  gate: VoiceGate;
  close: () => void;
}

export class ChunkRecorder {
  private recorder: MediaRecorder | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private chunkStartMs = 0;
  private running = false;
  private pipeline: GatePipeline | null = null;
  /** A darabban eddig elhangzott beszed (ms) - a nyitott kapu ideje. */
  private speechMs = 0;
  private openedAt: number | null = null;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;
  /** A MOSTANI darab a kapun at keszul-e (a kapu hangfeldolgozoja fut-e). */
  private chunkGated = false;
  /**
   * A darabban az ELSO beszed kezdete (a hivas idovonalan). Ez lesz a darab
   * kezdete - nem a felvetel inditasa, ami akar 20 masodperccel korabban volt.
   */
  private speechStartMs: number | null = null;

  constructor(private readonly options: RecorderOptions) {}

  get isRunning(): boolean {
    return this.running;
  }

  start(): boolean {
    if (this.running) return true;

    const mimeType = pickMimeType();
    if (!mimeType) {
      this.options.onError('Ez a böngésző nem tud hangot rögzíteni, így átirat sem készül.');
      return false;
    }

    this.running = true;
    if (this.options.gate !== false) this.pipeline = this.buildGate();
    this.cycle(mimeType);
    return this.running;
  }

  /**
   * A kapu felepitese: mikrofon -> kesleltetes -> erosites (0 vagy 1) -> felvetel,
   * mellette a szint-figyelo a kesleltetes NELKULI jelen.
   *
   * A figyelo a hang-szalon fut (ScriptProcessor): a hatterbe tett ablak
   * idozitoit a bongeszo lelassitja, a hangfeldolgozast nem.
   */
  private buildGate(): GatePipeline | null {
    try {
      const context = new AudioContext();
      void context.resume().catch(() => undefined);
      /*
        KATTINTAS NELKUL a bongeszo felfuggesztve inditja a hangfeldolgozast - a
        hivas ablakaban pedig nem biztos, hogy volt kattintas. Ilyenkor a darabok
        kapu nelkul keszulnek (lasd `cycle`), es az elso kattintas vagy
        billentyu elinditja a kaput: a kovetkezo darabtol mar szur.
      */
      const wake = () => void context.resume().catch(() => undefined);
      window.addEventListener('pointerdown', wake, true);
      window.addEventListener('keydown', wake, true);
      const source = context.createMediaStreamSource(this.options.stream);
      const delay = context.createDelay(1);
      delay.delayTime.value = GATE_DELAY_S;
      const gain = context.createGain();
      gain.gain.value = 0;
      const destination = context.createMediaStreamDestination();
      /*
        EGYCSATORNAS felvetel. A cel alapbol ket csatornat ad (a mono mikrofont
        ketszer), es a Google v1 ezt 400-zal utasitotta el ("audio_channel_count
        must match the WEBM OPUS header 2"). A beszedhez egy csatorna eleg, es a
        darab is kisebb.
      */
      destination.channelCount = 1;
      destination.channelCountMode = 'explicit';
      source.connect(delay).connect(gain).connect(destination);

      const gate = new VoiceGate();
      const size = 1024;
      const probe = context.createScriptProcessor(size, 1, 1);
      // A figyelonek a kimenetre kell kotve lennie, hogy fusson - nemitva.
      const sink = context.createGain();
      sink.gain.value = 0;
      source.connect(probe).connect(sink).connect(context.destination);
      probe.onaudioprocess = (event) => {
        const blockMs = (size / context.sampleRate) * 1000;
        const change = gate.push(blockLevelDb(event.inputBuffer.getChannelData(0)), blockMs);
        if (change === 'open') this.gateOpened();
        else if (change === 'close') this.gateClosed();
      };

      return {
        context,
        stream: destination.stream,
        gain,
        gate,
        close: () => {
          window.removeEventListener('pointerdown', wake, true);
          window.removeEventListener('keydown', wake, true);
          probe.onaudioprocess = null;
          source.disconnect();
          probe.disconnect();
          void context.close().catch(() => undefined);
        },
      };
    } catch {
      // Kapu nelkul is van atirat - csak a csend es az atszurodes marad benne.
      return null;
    }
  }

  private gateOpened(): void {
    const pipeline = this.pipeline;
    if (!pipeline) return;
    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }
    const now = pipeline.context.currentTime;
    pipeline.gain.gain.cancelScheduledValues(now);
    pipeline.gain.gain.setTargetAtTime(1, now, 0.005);
    this.openedAt ??= performance.now();
    /*
      A beszed kezdete: a dontes pillanata, minusz a kesleltetes (a felvetel a
      kesleltetett hangot kapja, tehat ennyivel korabbi hang kerul bele).
    */
    this.speechStartMs ??= Math.max(0, this.options.offsetMs() - GATE_DELAY_S * 1000);
    if (this.chunkGated && this.recorder?.state === 'paused') this.recorder.resume();
  }

  private gateClosed(): void {
    const pipeline = this.pipeline;
    if (!pipeline) return;
    if (this.openedAt !== null) {
      this.speechMs += performance.now() - this.openedAt;
      this.openedAt = null;
    }
    // A kesleltetett hang meg uton van: a mondat vege is keruljon be.
    const at = pipeline.context.currentTime + GATE_DELAY_S;
    pipeline.gain.gain.setTargetAtTime(0, at, 0.01);
    // Eleg beszed van a darabban: a szunet egy mondat vege - itt zarjuk a darabot.
    const endChunk = this.speechMs >= UTTERANCE_MIN_SPEECH_MS;
    this.pauseTimer = setTimeout(
      () => {
        this.pauseTimer = null;
        const recorder = this.recorder;
        if (!this.chunkGated || !recorder) return;
        if (endChunk && recorder.state !== 'inactive') {
          // A 25 mp-es idozito helyett most zarul; az `onstop` inditja a kovetkezot.
          if (this.timer) clearTimeout(this.timer);
          this.timer = null;
          recorder.stop();
        } else if (recorder.state === 'recording') {
          recorder.pause();
        }
      },
      GATE_DELAY_S * 1000 + 50,
    );
  }

  /** A darab beszedideje - a meg nyitott kapu ideje is beleszamit. */
  private takeSpeechMs(): number {
    let total = this.speechMs;
    if (this.openedAt !== null) {
      const now = performance.now();
      total += now - this.openedAt;
      this.openedAt = now;
    }
    this.speechMs = 0;
    return total;
  }

  stop(): void {
    this.running = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }

    const recorder = this.recorder;
    this.recorder = null;
    // A `stop()` meg kiadja az utolso darabot - azt is elkuldjuk.
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    this.pipeline?.close();
    this.pipeline = null;
  }

  private cycle(mimeType: string): void {
    if (!this.running) return;

    /*
      Csak akkor a kapun at, ha a kapu hangfeldolgozoja TENYLEG fut. Felfuggesztve
      a kapu kimenete nema - azt felvenni azt jelentene, hogy semmi nem kerul az
      atiratba. Ilyenkor inkabb a nyers mikrofon megy, mint a kapu elott.
    */
    const pipeline = this.pipeline;
    const gated = pipeline?.context.state === 'running';
    this.chunkGated = gated;
    this.speechMs = 0;
    this.openedAt = gated && pipeline.gate.open ? performance.now() : null;
    // Ha a kapu mar nyitva (a mondat a darabhatar folott folytatodik), innen indul.
    this.speechStartMs = gated && pipeline.gate.open ? this.options.offsetMs() : null;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(gated ? pipeline.stream : this.options.stream, { mimeType });
    } catch (error) {
      this.running = false;
      this.options.onError(
        error instanceof Error
          ? `A hangfelvétel nem indult el: ${error.message}`
          : 'A hangfelvétel nem indult el.',
      );
      return;
    }

    this.recorder = recorder;
    this.chunkStartMs = this.options.offsetMs();

    recorder.ondataavailable = (event: BlobEvent) => {
      /*
        A kapus darab a BESZED kezdetetol szamit: igy a kulonbozo beszelok
        mondatai a valodi sorrendjukben fesulodnek ossze.
      */
      const startMs = gated ? (this.speechStartMs ?? this.chunkStartMs) : this.chunkStartMs;
      this.speechStartMs = null;
      const durationMs = Math.max(1, this.options.offsetMs() - startMs);
      const speechMs = this.takeSpeechMs();

      /*
        BESZED NELKULI darab nem megy fel. Amibol a kapu semmit nem engedett at,
        abban nincs mit atirni - a modell csak kitalalna bele valamit.
      */
      if (gated && speechMs < MIN_SPEECH_MS) return;

      /*
        A TUL ROVID DARAB nem megy fel.

        A felvetel a mikrofonnal egyutt all le, ezert minden nemitas lezar egy
        darabot. Aki visszakapcsol, nem szol, es ujra nemit, azzal egy olyan
        blobot kuldene fel, amiben csak a fejlec van - a felismero azt nem
        tudja megnyitni ("Failed to decode audio."), es a szerver haromszor
        probalkozik vele, mielott feladja.

        Korabban csak a TELJESEN ures darabot dobtuk el (`size === 0`), egy
        harom-negyszaz bajtos, csupasz fejlec viszont atment. Az igy elveszo
        hang legfeljebb egy masodperc a nemitas peremen - annal a mostani ara
        (garantalt hiba, harom felesleges keres, egy hibajelentes) sokkal
        nagyobb.
      */
      if (event.data.size < TRANSCRIPT_CHUNK_MIN_BYTES || durationMs < TRANSCRIPT_CHUNK_MIN_MS) {
        return;
      }

      this.options.onChunk({
        blob: event.data,
        startMs,
        durationMs,
        mimeType,
      });
    };

    recorder.onerror = () => {
      this.options.onError('A hangfelvétel megszakadt, ezért az átirat hiányos lehet.');
      this.running = false;
    };

    // Ha meg futunk, az egyik darab vege a kovetkezo kezdete.
    recorder.onstop = () => {
      if (this.running) this.cycle(mimeType);
    };

    recorder.start();
    // Zart kapuval szunetelve indul: csend ne keruljon a darab elejere.
    if (gated && !pipeline.gate.open) recorder.pause();
    this.timer = setTimeout(() => {
      if (recorder.state !== 'inactive') recorder.stop();
    }, TRANSCRIPT_CHUNK_MS);
  }
}
